/* global axios */
import ApiClient from './ApiClient';

class SchedulingAPI extends ApiClient {
  constructor() {
    super('scheduling', { accountScoped: true });
  }

  // Professionals
  getProfessionals(activeOnly = false) {
    return axios.get(`${this.url}/professionals`, {
      params: { active_only: activeOnly || undefined },
    });
  }

  createProfessional(data) {
    return axios.post(`${this.url}/create_professional`, data);
  }

  showProfessional(id) {
    return axios.get(`${this.url}/${id}/show_professional`);
  }

  updateProfessional(id, data) {
    return axios.put(`${this.url}/${id}/update_professional`, data);
  }

  destroyProfessional(id) {
    return axios.delete(`${this.url}/${id}/destroy_professional`);
  }

  // Professional Hours
  getProfessionalHours(id) {
    return axios.get(`${this.url}/${id}/professional_hours`);
  }

  updateProfessionalHours(id, hours) {
    return axios.put(`${this.url}/${id}/update_professional_hours`, { hours });
  }

  // Available Slots
  getAvailableSlots(date, professionalId = null) {
    const params = { date };
    if (professionalId) params.professional_id = professionalId;
    return axios.get(`${this.url}/available_slots`, { params });
  }

  getProfessionalSlots(id, date) {
    return axios.get(`${this.url}/${id}/professional_slots`, {
      params: { date },
    });
  }

  // Bookings
  getBookings(params = {}) {
    return axios.get(`${this.url}/bookings`, { params });
  }

  createBooking(data) {
    return axios.post(`${this.url}/create_booking`, data);
  }

  showBooking(id) {
    return axios.get(`${this.url}/${id}/show_booking`);
  }

  updateBooking(id, data) {
    return axios.put(`${this.url}/${id}/update_booking`, data);
  }

  cancelBooking(id) {
    return axios.post(`${this.url}/${id}/cancel_booking`);
  }

  sendConfirmation(id) {
    return axios.post(`${this.url}/${id}/send_confirmation`);
  }

  // Services (account-wide catalog)
  getServices() {
    return axios.get(`${this.url}/services`);
  }

  createService(data) {
    return axios.post(`${this.url}/create_service`, data);
  }

  updateService(id, data) {
    return axios.put(`${this.url}/${id}/update_service`, data);
  }

  destroyService(id) {
    return axios.delete(`${this.url}/${id}/destroy_service`);
  }

  // Professional Services (per-professional service config)
  getProfessionalServices(profId) {
    return axios.get(`${this.url}/${profId}/professional_services`);
  }

  updateProfessionalServices(profId, services) {
    return axios.put(`${this.url}/${profId}/update_professional_services`, {
      services,
    });
  }

  // Packages
  getPackages() {
    return axios.get(`${this.url}/packages`);
  }

  createPackage(data) {
    return axios.post(`${this.url}/create_package`, data);
  }

  showPackage(id) {
    return axios.get(`${this.url}/${id}/show_package`);
  }

  updatePackage(id, data) {
    return axios.put(`${this.url}/${id}/update_package`, data);
  }

  destroyPackage(id) {
    return axios.delete(`${this.url}/${id}/destroy_package`);
  }

  // Roles
  getRoles() {
    return axios.get(`${this.url}/roles`);
  }

  createRole(data) {
    return axios.post(`${this.url}/create_role`, data);
  }

  updateRole(id, data) {
    return axios.put(`${this.url}/${id}/update_role`, data);
  }

  destroyRole(id) {
    return axios.delete(`${this.url}/${id}/destroy_role`);
  }

  // Booking Payments
  getBookingPayments(bookingId) {
    return axios.get(`${this.url}/${bookingId}/booking_payments`);
  }

  createBookingPayment(bookingId, payments) {
    return axios.post(`${this.url}/${bookingId}/create_booking_payment`, {
      payments,
    });
  }

  // Cash Closing
  getCashClosing(params = {}) {
    return axios.get(`${this.url}/cash_closing`, { params });
  }

  // Financial Report
  getFinancialReport(params = {}) {
    return axios.get(`${this.url}/financial_report`, { params });
  }

  // Commission Report
  getCommissionReport(params = {}) {
    return axios.get(`${this.url}/commission_report`, { params });
  }

  // Goals
  getGoals() {
    return axios.get(`${this.url}/goals`);
  }

  createGoal(data) {
    return axios.post(`${this.url}/create_goal`, data);
  }

  updateGoal(id, data) {
    return axios.put(`${this.url}/${id}/update_goal`, data);
  }

  destroyGoal(id) {
    return axios.delete(`${this.url}/${id}/destroy_goal`);
  }

  // Payment Config
  getPaymentConfig() {
    return axios.get(`${this.url}/payment_config`);
  }

  updatePaymentConfig(data) {
    return axios.put(`${this.url}/update_payment_config`, {
      payment_methods: data,
    });
  }

  // Blocks
  getBlocks(params = {}) {
    return axios.get(`${this.url}/blocks`, { params });
  }

  createBlock(data) {
    return axios.post(`${this.url}/create_block`, data);
  }

  destroyBlock(blockId) {
    return axios.delete(`${this.url}/${blockId}/destroy_block`);
  }

  // Stock Management
  getStockProducts(params = {}) {
    return axios.get(`${this.url}/stock_products`, { params });
  }

  getStockMovements(params = {}) {
    return axios.get(`${this.url}/stock_movements`, { params });
  }

  createStockMovement(data) {
    return axios.post(`${this.url}/create_stock_movement`, data);
  }

  sellProducts(data) {
    return axios.post(`${this.url}/sell_products`, data);
  }

  sendCheckinLink(bookingId) {
    return axios.post(`${this.url}/${bookingId}/send_checkin_link`);
  }

  // Package Sales
  sellPackage(data) {
    return axios.post(`${this.url}/sell_package`, data);
  }

  getContactPackageSales(contactId, availableOnly = false) {
    return axios.get(`${this.url}/contact_package_sales`, {
      params: { contact_id: contactId, available_only: availableOnly || undefined },
    });
  }

  showPackageSale(id) {
    return axios.get(`${this.url}/${id}/show_package_sale`);
  }

  cancelPackageSale(id) {
    return axios.post(`${this.url}/${id}/cancel_package_sale`);
  }

  // Pending sessions for a contact
  getContactPendingSessions(contactId) {
    return axios.get(`${this.url}/contact_pending_sessions`, {
      params: { contact_id: contactId },
    });
  }

  // Session Completion (per booking item)
  completeSession(bookingId, itemId) {
    return axios.post(`${this.url}/${bookingId}/complete_session`, {
      item_id: itemId,
    });
  }

  undoCompleteSession(bookingId, itemId) {
    return axios.post(`${this.url}/${bookingId}/undo_complete_session`, {
      item_id: itemId,
    });
  }

  // Check-ins
  getCheckins(params = {}) {
    return axios.get(`${this.url}/checkins`, { params });
  }

  // Upload avatar (multipart)
  updateProfessionalWithAvatar(id, formData) {
    return axios.put(`${this.url}/${id}/update_professional`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  createProfessionalWithAvatar(formData) {
    return axios.post(`${this.url}/create_professional`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  // Service photo upload (multipart)
  createServiceWithPhoto(formData) {
    return axios.post(`${this.url}/create_service`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  updateServiceWithPhoto(id, formData) {
    return axios.put(`${this.url}/${id}/update_service`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  // Clients
  getClients(params = {}) {
    return axios.get(`${this.url}/clients`, { params });
  }

  getClientDetail(contactId) {
    return axios.get(`${this.url}/client_detail`, {
      params: { contact_id: contactId },
    });
  }

  // Financial Accounts (Contas a Pagar/Receber)
  getFinancialAccounts(params = {}) {
    return axios.get(`${this.url}/financial_accounts`, { params });
  }

  createFinancialAccount(data) {
    return axios.post(`${this.url}/create_financial_account`, data);
  }

  updateFinancialAccount(id, data) {
    return axios.put(`${this.url}/${id}/update_financial_account`, data);
  }

  destroyFinancialAccount(id) {
    return axios.delete(`${this.url}/${id}/destroy_financial_account`);
  }

  markFinancialAccountPaid(id) {
    return axios.post(`${this.url}/${id}/mark_financial_account_paid`);
  }

  // Financial Categories
  getFinancialCategories() {
    return axios.get(`${this.url}/financial_categories`);
  }

  createFinancialCategory(data) {
    return axios.post(`${this.url}/create_financial_category`, data);
  }

  updateFinancialCategory(id, data) {
    return axios.put(`${this.url}/${id}/update_financial_category`, data);
  }

  destroyFinancialCategory(id) {
    return axios.delete(`${this.url}/${id}/destroy_financial_category`);
  }

  // Reviews
  getReviews(params = {}) {
    return axios.get(`${this.url}/reviews`, { params });
  }

  createReview(data) {
    return axios.post(`${this.url}/create_review`, data);
  }

  destroyReview(id) {
    return axios.delete(`${this.url}/${id}/destroy_review`);
  }

  toggleReviewVisibility(id) {
    return axios.post(`${this.url}/${id}/toggle_review_visibility`);
  }
}

export default new SchedulingAPI();
